/**
 * 设备使用记录服务
 */
import api from './api';
import {
  EquipmentUsage,
  CreateEquipmentUsageData,
  UpdateEquipmentUsageData,
  EquipmentUsageQueryParams,
  PaginatedResponse,
  ApiResponse,
  EquipmentUsageSummary,
} from '../types';

export class EquipmentUsageService {
  /**
   * 获取设备使用记录列表
   */
  static async getEquipmentUsages(
    params: EquipmentUsageQueryParams = {}
  ): Promise<PaginatedResponse<EquipmentUsage>> {
    const response = await api.get('/equipment-usage', { params });
    return response as unknown as PaginatedResponse<EquipmentUsage>;
  }

  /**
   * 根据ID获取设备使用记录
   */
  static async getEquipmentUsageById(id: string): Promise<ApiResponse<EquipmentUsage>> {
    const response = await api.get(`/equipment-usage/${id}`);
    return response as unknown as ApiResponse<EquipmentUsage>;
  }

  /**
   * 创建设备使用记录
   */
  static async createEquipmentUsage(
    data: CreateEquipmentUsageData
  ): Promise<ApiResponse<EquipmentUsage>> {
    const response = await api.post('/equipment-usage', data);
    return response as unknown as ApiResponse<EquipmentUsage>;
  }

  /**
   * 更新设备使用记录
   */
  static async updateEquipmentUsage(
    id: string,
    data: UpdateEquipmentUsageData
  ): Promise<ApiResponse<EquipmentUsage>> {
    const response = await api.put(`/equipment-usage/${id}`, data);
    return response as unknown as ApiResponse<EquipmentUsage>;
  }

  /**
   * 删除设备使用记录
   */
  static async deleteEquipmentUsage(id: string): Promise<ApiResponse> {
    const response = await api.delete(`/equipment-usage/${id}`);
    return response as unknown as ApiResponse;
  }

  /**
   * 获取设备使用统计
   */
  static async getEquipmentUsageSummary(
    params: EquipmentUsageQueryParams = {}
  ): Promise<ApiResponse<EquipmentUsageSummary>> {
    const response = await api.get('/equipment-usage/summary', { params });
    return response as unknown as ApiResponse<EquipmentUsageSummary>;
  }

  /**
   * 获取设备使用报表
   */
  static async getEquipmentUsageReport(
    params: EquipmentUsageQueryParams = {}
  ): Promise<ApiResponse<any>> {
    const response = await api.get('/equipment-usage/report', { params });
    return response as unknown as ApiResponse<any>;
  }

  /**
   * 导出设备使用列表Excel
   */
  static async exportExcel(params: EquipmentUsageQueryParams = {}): Promise<void> {
    try {
      const response = await api.get('/equipment-usage/export/excel', { 
        params,
        responseType: 'blob' 
      });
      // api拦截器已经返回了response.data，所以这里response就是Blob对象
      this.downloadFile(response as unknown as Blob, '设备使用记录列表.xlsx');
    } catch (error) {
      console.error('导出Excel失败:', error);
      throw error;
    }
  }

  /**
   * 导出设备使用列表PDF
   */
  static async exportPDF(params: EquipmentUsageQueryParams = {}): Promise<void> {
    try {
      const response = await api.get('/equipment-usage/export/pdf', { 
        params,
        responseType: 'blob' 
      });
      // api拦截器已经返回了response.data，所以这里response就是Blob对象
      this.downloadFile(response as unknown as Blob, '设备使用记录列表.pdf');
    } catch (error) {
      console.error('导出PDF失败:', error);
      throw error;
    }
  }

  /**
   * 通用文件下载方法
   */
  private static downloadFile(data: Blob, filename: string) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * 获取设备名称列表（用于自动完成）
   */
  static async getEquipmentNames(): Promise<ApiResponse<string[]>> {
    const response = await api.get('/equipment-usage/equipment-names');
    return response as unknown as ApiResponse<string[]>;
  }
}